import { Inject, Injectable, Optional, forwardRef } from '@angular/core';
import {
  BestResultInterface,
  ContentInterface,
  ContextInterface,
  DalState,
  EduContent,
  EduContentTypeEnum,
  Result,
  ResultInterface,
  ResultStatusEnum,
} from '@campus/dal';
import { ArrayFunctions } from '@campus/utils';
import { Store } from '@ngrx/store';
import { StudentTaskWithContentInterface } from '../../../interfaces';
import { ContentActionHandlerParamsType, ContentActionInterface } from '../content-action.interface';
import { ContentOpenActionsBaseService } from './content-open-actions.base.service';
import {
  ActionsForTaskInstanceEduContentOptionsInterface,
  ContentActionDictionary,
  ContentActionType,
  ContentOpenActionsServiceInterface,
  ContentOpenerInterface,
  GetActionsForContentConfigInterface,
  OpenContentOptions,
  STUDENT_CONTENT_OPENER_TOKEN,
} from './content-open-actions.service.interface';
import { DefaultContentOpenerService } from './default-content-opener.service';

@Injectable({
  providedIn: 'root',
})
export class ContentOpenActionsStudentService
  extends ContentOpenActionsBaseService
  implements ContentOpenActionsServiceInterface
{
  constructor(
    @Inject(forwardRef(() => STUDENT_CONTENT_OPENER_TOKEN))
    @Optional()
    protected contentOpener: ContentOpenerInterface,
    @Inject(DefaultContentOpenerService)
    protected defaultContentOpener: ContentOpenerInterface,
    public store: Store<DalState>
  ) {
    super(contentOpener, defaultContentOpener, ContentOpenActionsStudentService.contentActionDictionary, store);
  }

  private static readonly contentActionDictionary: ContentActionDictionary = {
    openEduContentAsExercise: {
      type: 'openEduContentAsExercise',
      label: 'Openen',
      icon: 'open-in-new',
      tooltip: 'Open oefening',
      handlerMethod: 'openEduContentAsExercise',
      // handler: this.getHandler('openEduContentAsExercise', data),
      isPrimary: true,
    },
    continueEduContentAsExercise: {
      type: 'continueEduContentAsExercise',
      label: 'Verder werken',
      icon: 'continue',
      tooltip: 'Open oefening',
      showAsBadge: true,
      handlerMethod: 'openEduContentAsExercise',
      // handler: this.getHandler('openEduContentAsExercise', data),
      isPrimary: true,
    },
    retryEduContentAsExercise: {
      type: 'retryEduContentAsExercise',
      label: 'Maak opnieuw',
      icon: 'refresh',
      tooltip: 'Open oefening',
      showAsBadge: true,
      handlerMethod: 'openEduContentAsExercise',
      // handler: this.getHandler('openEduContentAsExercise', data),
      isPrimary: true,
    },
    openEduContentForReview: {
      type: 'openEduContentForReview',
      label: 'Herbekijk oefening',
      icon: 'preview',
      tooltip: 'Open oefening met oplossingen',
      handlerMethod: 'openEduContentForReview',
      // handler: this.getHandler('openEduContentForReview', data),
      isPrimary: true,
    },
    openEduContentAsSolution: {
      label: 'Herbekijk oefening',
      icon: 'preview',
      tooltip: 'Open oefening met oplossingen',
      handlerMethod: 'openEduContentAsSolution',
      // handler: this.getHandler('openEduContentAsSolution'),
      isPrimary: true,
    },
    openEduContentAsStream: {
      type: 'openEduContentAsStream',
      label: 'Bekijken',
      icon: 'open-in-new',
      tooltip: 'Bekijk de video',
      handlerMethod: 'openEduContentAsStream',
      // handler: this.getHandler('openEduContentAsStream', data),
      isPrimary: true,
    },
    openEduContentAsStreamCompleted: {
      type: 'openEduContentAsStreamCompleted',
      label: 'Opnieuw bekijken',
      icon: 'open-in-new',
      tooltip: 'Bekijk de video opnieuw',
      handlerMethod: 'openEduContentAsStream',
      // handler: this.getHandler('openEduContentAsStream', data),
      isPrimary: true,
    },
    openEduContentAsDownload: {
      type: 'openEduContentAsDownload',
      label: 'Openen',
      icon: 'open-in-new',
      tooltip: 'Open het lesmateriaal',
      handlerMethod: 'openEduContentAsDownload',
      // handler: this.getHandler('openEduContentAsDownload', data),
      isPrimary: true,
    },
    openEduContentAsDownloadCompleted: {
      type: 'openEduContentAsDownloadCompleted',
      label: 'Openen',
      icon: 'open-in-new',
      tooltip: 'Open het lesmateriaal opnieuw',
      handlerMethod: 'openEduContentAsDownload',
      // handler: this.getHandler('openEduContentAsDownload', data),
      isPrimary: true,
    },
    openEduContentAsLink: {
      type: 'openEduContentAsLink',
      label: 'Openen',
      icon: 'open-in-new',
      tooltip: 'Open de link',
      handlerMethod: 'openEduContentAsLink',
      // handler: this.getHandler('openEduContentAsLink', data),
      isPrimary: true,
    },
    openEduContentAsLinkCompleted: {
      type: 'openEduContentAsLinkCompleted',
      label: 'Opnieuw openen',
      icon: 'open-in-new',
      tooltip: 'Open de link opnieuw',
      handlerMethod: 'openEduContentAsLink',
      // handler: this.getHandler('openEduContentAsLink', data),
      isPrimary: true,
    },
    openEduContentAsWebApp: {
      type: 'openEduContentAsWebApp',
      label: 'Openen',
      icon: 'open-in-new',
      tooltip: 'Open de web-app',
      handlerMethod: 'openEduContentAsLink',
      // handler: this.getHandler('openEduContentAsLink', data),
      isPrimary: true,
    },
    openEduContentAsWebAppCompleted: {
      type: 'openEduContentAsWebAppCompleted',
      label: 'Opnieuw openen',
      icon: 'open-in-new',
      tooltip: 'Open de web-app opnieuw',
      handlerMethod: 'openEduContentAsLink',
      // handler: this.getHandler('openEduContentAsLink', data),
      isPrimary: true,
    },
    openBoeke: {
      type: 'openBoeke',
      label: 'Openen',
      icon: 'open-in-new',
      tooltip: 'Open het bordboek',
      handlerMethod: 'openBoeke',
      // handler: this.getHandler('openBoeke', data),
      isPrimary: true,
    },
    openPaperExercise: {
      type: 'openPaperExercise',
      label: 'Openen',
      icon: 'open-in-new',
      tooltip: 'Open de oefening',
      handlerMethod: 'openPaperExercise',
      // handler: this.getHandler('openPaperExercise', data),
      isPrimary: true,
    },
    openEduContentAsHtml: {
      type: 'openEduContentAsHtml',
      label: 'Lezen',
      icon: 'preview',
      tooltip: 'Lees de tekst',
      handlerMethod: 'openEduContentAsHtml',
      // handler: this.getHandler('openEduContentAsHtml', data),
      isPrimary: true,
    },
    openInstructions: {
      type: 'openInstructions',
      label: 'Uitleg bekijken',
      icon: 'open-in-new',
      tooltip: 'Open de uitleg',
      handlerMethod: 'openInstructions',
      // handler: this.getHandler('openInstructions', data),
    },
  };

  /**
   * Gets all actions for the provided eduContent
   *
   * @param {EduContent} content
   * @returns {ContentActionInterface[]}
   * @memberof ContentOpenActionsStudentService
   */
  getActionsForContent(
    content: ContentInterface,
    result?: ResultInterface,
    options: { forReview?: boolean; forDWB?: boolean } = { forReview: false, forDWB: false },
    openContentOptions: OpenContentOptions = {}
  ): ContentActionInterface[] {
    const actionTypes = this.getEduContentActions(content, result, options);

    const args = this.getHandlerArgs(content, openContentOptions, result);
    return actionTypes.map((actionType) => this.createContentActionWithHandler(actionType, args));
  }

  getActionsForContentNew<T = ContentActionInterface>(
    content: ContentInterface,
    attempts: ResultInterface[],
    openContentOptions: OpenContentOptions = {},
    context: ContextInterface = {},
    config: GetActionsForContentConfigInterface<T> = {}
  ): T[] {
    const bestResult = Result.getBestResult(attempts);
    const lastResult = Result.getLastResult(attempts, { key: 'lastUpdated' });

    // all possible actions for this content based on its type and results
    const actionTypes = this.getActionTypesForContent(content, bestResult, lastResult);

    // provide defaults for config functions
    const {
      mapFn = (action: ContentActionInterface) => action as T,
      filterFn = () => true,
      sortFn = ArrayFunctions.sortByKey('displayOrder', 'ASC'),
    } = config;

    const handlerArgs = this.getHandlerArgs(content, openContentOptions, bestResult, context);

    return actionTypes
      .map((actionType) => this.createContentActionWithHandler(actionType, handlerArgs))
      .filter(filterFn)
      .map(mapFn)
      .sort(sortFn) as T[];
  }

  getActionForContent(content: ContentInterface, actionType: ContentActionType): ContentActionInterface | undefined {
    const actions = this.getActionsForContent(content);
    const action = actions.find((_action) => _action.type === actionType);
    return action;
  }

  getActionsForTaskInstanceEduContent(
    eduContent: EduContent,
    result: BestResultInterface,
    taskInstance: Pick<StudentTaskWithContentInterface, 'end' | 'isFinished'>,
    options?: ActionsForTaskInstanceEduContentOptionsInterface
  ): ContentActionInterface[] {
    const actionTypes = eduContent.isExercise
      ? this.getActionTypesForTaskInstanceEduContent(result, taskInstance, options)
      : this.getEduContentActions(eduContent, result);

    const args = this.getHandlerArgs(eduContent, {}, result);

    return actionTypes.map((actionType) => this.createContentActionWithHandler(actionType, args));
  }

  getActionsForTaskInstanceEduContentNew(
    eduContent: EduContent,
    result: BestResultInterface,
    taskInstance: Pick<StudentTaskWithContentInterface, 'end' | 'isFinished' | 'id'>,
    options?: ActionsForTaskInstanceEduContentOptionsInterface,
    openContentOptions: OpenContentOptions = {}
  ): ContentActionInterface[] {
    const actionTypes = eduContent.isExercise
      ? this.getActionTypesForTaskInstanceEduContent(result, taskInstance, options)
      : this.getEduContentActions(eduContent, result);
    const context = { taskId: taskInstance.id };
    const args = this.getHandlerArgs(
      eduContent,
      {
        ...openContentOptions,
        withFeedback: options?.withFeedback,
        isRepeatable: options?.isRepeatable,
      },
      result,
      context
    );

    return actionTypes.map((actionType) => this.createContentActionWithHandler(actionType, args));
  }

  private getActionTypesForTaskInstanceEduContent(
    result: BestResultInterface,
    taskInstance: Pick<StudentTaskWithContentInterface, 'end' | 'isFinished'>,
    options?: ActionsForTaskInstanceEduContentOptionsInterface
  ): ContentActionType[] {
    const isTaskInstanceInPast = taskInstance.isFinished || taskInstance.end < new Date();
    const attempts = (result?.attempts || [result]).filter(Boolean); // if not bestResult, it's a regular result
    const mostRecentAttempt = Result.getLastResult(attempts, { key: 'lastUpdated' });
    const isTaskEduContentCompleted = attempts.some((attempt) => attempt?.status === ResultStatusEnum.STATUS_COMPLETED);
    const isMostRecentAttemptCompleted = mostRecentAttempt?.status === ResultStatusEnum.STATUS_COMPLETED;
    const isMostRecentAttemptIncomplete = mostRecentAttempt?.status === ResultStatusEnum.STATUS_INCOMPLETE;

    // If an exercise is ended show the review button
    if (isTaskInstanceInPast) {
      if (!result) {
        return ['openEduContentAsSolution'];
      }
      return ['openEduContentForReview'];
    } else if (isTaskEduContentCompleted) {
      const feedbackAndOrRepeatableActions = [];

      if (options?.isRepeatable) {
        feedbackAndOrRepeatableActions.push(
          isMostRecentAttemptCompleted ? 'retryEduContentAsExercise' : 'continueEduContentAsExercise'
        );
      }
      if (options?.withFeedback) {
        feedbackAndOrRepeatableActions.push('openEduContentForReview');
      }

      return feedbackAndOrRepeatableActions;
    } else if (isMostRecentAttemptIncomplete) {
      return ['continueEduContentAsExercise'];
    } else {
      return ['openEduContentAsExercise'];
    }
  }

  private getEduContentActions(
    content: ContentInterface,
    result: Pick<ResultInterface, 'status'>,
    options: {
      forReview?: boolean;
      forDWB?: boolean;
    } = { forReview: false, forDWB: false }
  ): ContentActionType[] {
    if (!content) return [];

    const completed =
      result && [ResultStatusEnum.STATUS_COMPLETED, ResultStatusEnum.STATUS_OPENED].includes(result.status);

    // Special case that covers eduContent.type exercise or usesScorm, skipping the switch case below:
    if (content instanceof EduContent && (content as EduContent).isExercise) {
      const actionTypes = options.forDWB
        ? this.getExerciseContentActionsForDWB(result, completed)
        : this.getExerciseContentActions(result, completed, options.forReview);

      return actionTypes;
    }

    switch (content.type) {
      case EduContentTypeEnum.BOEKE:
        return ['openBoeke'];
      case EduContentTypeEnum.PAPER_EXERCISE:
        return ['openPaperExercise'];
      case EduContentTypeEnum.LINK:
        return completed ? ['openEduContentAsLinkCompleted'] : ['openEduContentAsLink'];
      case EduContentTypeEnum.WEB_APP: {
        const webAppActions = [];
        if (completed) webAppActions.push('openEduContentAsWebAppCompleted');
        else webAppActions.push('openEduContentAsWebApp');

        if (content instanceof EduContent && content.hasInstructions) {
          webAppActions.push('openInstructions');
        }
        return webAppActions;
      }
      case EduContentTypeEnum.TEXT:
        return ['openEduContentAsHtml'];
    }

    if (content instanceof EduContent && content.streamable) {
      return completed ? ['openEduContentAsStreamCompleted'] : ['openEduContentAsStream'];
    } else {
      return completed ? ['openEduContentAsDownloadCompleted'] : ['openEduContentAsDownload'];
    }
  }
  private getActionTypesForContent(
    content: ContentInterface,
    bestResult: Pick<ResultInterface, 'status'>,
    lastResult: Pick<ResultInterface, 'status'>
  ): ContentActionType[] {
    if (!content) return [];

    const lastResultCompleted =
      lastResult && [ResultStatusEnum.STATUS_COMPLETED, ResultStatusEnum.STATUS_OPENED].includes(lastResult.status);

    // Special case that covers eduContent.type exercise or usesScorm, skipping the switch case below:
    if (content instanceof EduContent && (content as EduContent).isExercise) {
      const actionTypes = this.getExerciseContentActionsNew(bestResult, lastResult);

      return actionTypes;
    }

    switch (content.type) {
      case EduContentTypeEnum.BOEKE:
        return ['openBoeke'];
      case EduContentTypeEnum.PAPER_EXERCISE:
        return ['openPaperExercise'];
      case EduContentTypeEnum.LINK:
        return lastResultCompleted ? ['openEduContentAsLinkCompleted'] : ['openEduContentAsLink'];
      case EduContentTypeEnum.WEB_APP: {
        const webAppActions = [];
        if (lastResultCompleted) webAppActions.push('openEduContentAsWebAppCompleted');
        else webAppActions.push('openEduContentAsWebApp');

        if (content instanceof EduContent && content.hasInstructions) {
          webAppActions.push('openInstructions');
        }
        return webAppActions;
      }
      case EduContentTypeEnum.TEXT:
        return ['openEduContentAsHtml'];
    }

    if (content instanceof EduContent && content.streamable) {
      return lastResultCompleted ? ['openEduContentAsStreamCompleted'] : ['openEduContentAsStream'];
    } else {
      return lastResultCompleted ? ['openEduContentAsDownloadCompleted'] : ['openEduContentAsDownload'];
    }
  }

  private getExerciseContentActions(
    result: ResultInterface,
    completed: boolean,
    forReview?: boolean
  ): ContentActionType[] {
    if (forReview) {
      return ['openEduContentForReview'];
    }
    if (completed) {
      return ['retryEduContentAsExercise'];
    }
    if (result && result.status === ResultStatusEnum.STATUS_INCOMPLETE) {
      return ['continueEduContentAsExercise'];
    }
    return ['openEduContentAsExercise'];
  }

  private getExerciseContentActionsNew(
    bestResult: Pick<ResultInterface, 'status'>,
    lastResult: Pick<ResultInterface, 'status'>
  ): ContentActionType[] {
    const actions: ContentActionType[] = [];
    const bestResultCompleted =
      bestResult && [ResultStatusEnum.STATUS_COMPLETED, ResultStatusEnum.STATUS_OPENED].includes(bestResult.status);
    const lastResultInComplete = lastResult && [ResultStatusEnum.STATUS_INCOMPLETE].includes(lastResult.status);
    const lastResultCompleted =
      lastResult && [ResultStatusEnum.STATUS_COMPLETED, ResultStatusEnum.STATUS_OPENED].includes(lastResult.status);

    if (bestResultCompleted) {
      actions.push('openEduContentForReview');
    }
    if (lastResultCompleted) {
      actions.push('retryEduContentAsExercise');
    } else if (lastResultInComplete) {
      actions.push('continueEduContentAsExercise');
    } else {
      actions.push('openEduContentAsExercise');
    }

    return actions;
  }

  private getExerciseContentActionsForDWB(result: ResultInterface, completed: boolean): ContentActionType[] {
    if (completed) {
      return ['retryEduContentAsExercise', 'openEduContentForReview'];
    }
    if (result && result.status === ResultStatusEnum.STATUS_INCOMPLETE) {
      return ['continueEduContentAsExercise'];
    }
    return ['openEduContentAsExercise'];
  }

  private getHandlerArgs(
    content: ContentInterface,
    options: OpenContentOptions,
    bestResult: ResultInterface,
    context: ContextInterface = {}
  ): ContentActionHandlerParamsType {
    return [content as EduContent, { ...options, forResult: bestResult }, context];
  }
}
